import { useAuth } from "@clerk/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthFetch } from "./clerk.service";

const WS_URL = process.env.REACT_APP_WS_CHAT_URL;

/**
 * Connects to /ws/chat, authenticating with the Clerk JWT passed as the
 * WebSocket's sole subprotocol - a real browser's WebSocket constructor
 * can't set an Authorization header on the handshake, so this is the
 * standard way to get header-like auth on a browser WebSocket.
 *
 * Persists nothing itself - the backend owns session/history persistence
 * and sends it back right after connecting. startFresh() deletes the
 * history server-side, then reconnects for a brand-new empty session.
 */
export const useChatSocket = () => {
  const { getToken } = useAuth();
  const authFetch = useAuthFetch();
  const [messages, setMessages] = useState([]); // [{role, type, content}]
  const [progress, setProgress] = useState(null); // real tool-call description, or null
  const [streamingText, setStreamingText] = useState(null); // the in-flight answer, token-by-token
  const [isWaiting, setIsWaiting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [connectNonce, setConnectNonce] = useState(0); // bump to force a reconnect
  const wsRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let ws;

    const connect = async () => {
      const token = await getToken();
      if (cancelled || !token) return;

      ws = new WebSocket(WS_URL, [token]);
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onerror = () => setConnected(false);

      ws.onmessage = (event) => {
        const frame = JSON.parse(event.data);

        if (frame.type === "session") {
          setSessionId(frame.session_id);
          return;
        }

        if (frame.type === "history") {
          setMessages(
            frame.messages
              .filter((m) => m.message_type === "message")
              .map((m) => ({ role: m.role, type: m.message_type, content: m.content })),
          );
          return;
        }

        if (frame.type === "progress") {
          setProgress(frame.content);
          return;
        }

        if (frame.type === "delta") {
          // Real answer text has started arriving - the synthetic tool-progress
          // line has done its job, replace it with the actual streaming text.
          setProgress(null);
          setStreamingText((prev) => (prev || "") + frame.content);
          return;
        }

        // "message" | "rating" | "error" - the agent is done with this turn
        setIsWaiting(false);
        setProgress(null);
        setStreamingText(null);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: frame.type, content: frame.content },
        ]);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (ws) ws.close();
    };
  }, [getToken, connectNonce]);

  const sendMessage = useCallback((text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    setMessages((prev) => [...prev, { role: "user", type: "message", content: trimmed }]);
    setIsWaiting(true);
    setProgress(null);
    setStreamingText(null);
    wsRef.current.send(JSON.stringify({ message: trimmed, context: {} }));
  }, []);

  const reconnect = useCallback(() => {
    setMessages([]);
    setProgress(null);
    setStreamingText(null);
    setIsWaiting(false);
    setSessionId(null);
    setConnectNonce((n) => n + 1);
  }, []);

  const startFresh = useCallback(async () => {
    const res = await authFetch("/chat/session", { method: "DELETE" });
    if (!res.ok) throw new Error(`Failed to clear chat history: ${res.status}`);
    reconnect();
  }, [authFetch, reconnect]);

  return { messages, progress, streamingText, isWaiting, connected, sessionId, sendMessage, startFresh };
};
